import { useState } from 'react';

function ProfilesTab({ profiles, exportProfilesCSV }) {
  const [searchProfiles, setSearchProfiles] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PROFILES_PER_PAGE = 20;

  const filteredProfiles = profiles.filter(p => 
    !searchProfiles || 
    p.username.toLowerCase().includes(searchProfiles.toLowerCase()) ||
    p.aiInterest.toLowerCase().includes(searchProfiles.toLowerCase()) ||
    p.expertise.toLowerCase().includes(searchProfiles.toLowerCase())
  );

  const paginatedProfiles = filteredProfiles.slice(
    (currentPage - 1) * PROFILES_PER_PAGE,
    currentPage * PROFILES_PER_PAGE
  );

  const todayCount = profiles.filter(p => 
    new Date(p.completedAt).toDateString() === new Date().toDateString()
  ).length;

  const weekCount = profiles.filter(p => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return new Date(p.completedAt) >= weekAgo;
  }).length;

  const monthCount = profiles.filter(p => {
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return new Date(p.completedAt) >= monthAgo;
  }).length;

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-6">Thông tin hồ sơ người dùng</h2>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <span className="material-symbols-rounded text-4xl opacity-80 mb-4 block">badge</span>
          <h3 className="text-sm opacity-90 mb-1">Tổng hồ sơ</h3>
          <p className="text-3xl font-bold">{profiles.length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <span className="material-symbols-rounded text-4xl opacity-80 mb-4 block">today</span>
          <h3 className="text-sm opacity-90 mb-1">Hôm nay</h3>
          <p className="text-3xl font-bold">{todayCount}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <span className="material-symbols-rounded text-4xl opacity-80 mb-4 block">calendar_month</span>
          <h3 className="text-sm opacity-90 mb-1">Tuần này</h3>
          <p className="text-3xl font-bold">{weekCount}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <span className="material-symbols-rounded text-4xl opacity-80 mb-4 block">trending_up</span>
          <h3 className="text-sm opacity-90 mb-1">Tháng này</h3>
          <p className="text-3xl font-bold">{monthCount}</p>
        </div>
      </div>

      {/* Profiles Table */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Danh sách hồ sơ</h3>
          <div className="flex items-center gap-3">
            <button 
              onClick={exportProfilesCSV}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold flex items-center gap-2"
            >
              <span className="material-symbols-rounded text-[18px]">download</span>
              Xuất CSV
            </button>
            <input 
              type="text" 
              value={searchProfiles} 
              onChange={(e) => { setSearchProfiles(e.target.value); setCurrentPage(1); }} 
              placeholder="Tìm kiếm..." 
              className="px-4 py-2 border rounded-lg focus:outline-none focus:border-blue-500" 
            />
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Username</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Loại người dùng</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sở thích AI</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Chuyên môn</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ngày hoàn thành</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {paginatedProfiles.map(profile => (
                <tr key={profile._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{profile.username}</td>
                  <td className="px-4 py-3 text-sm">{profile.userType}</td>
                  <td className="px-4 py-3 text-sm max-w-xs truncate" title={profile.aiInterest}>{profile.aiInterest}</td>
                  <td className="px-4 py-3 text-sm">{profile.expertise}</td>
                  <td className="px-4 py-3 text-sm">{new Date(profile.completedAt).toLocaleDateString('vi-VN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {Math.ceil(filteredProfiles.length / PROFILES_PER_PAGE) > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-600">
              Trang {currentPage} / {Math.ceil(filteredProfiles.length / PROFILES_PER_PAGE)}
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                disabled={currentPage === 1}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Trước
              </button>
              <button 
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredProfiles.length / PROFILES_PER_PAGE), p + 1))} 
                disabled={currentPage === Math.ceil(filteredProfiles.length / PROFILES_PER_PAGE)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sau
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilesTab;
